import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { catchError, forkJoin, map, Observable, of, retry, timer } from 'rxjs';
import { environment } from '../../../environments/environment';

// Les traductions sont découpées par domaine sous /assets/i18n/<lang>/<domaine>.json
// (les clés y restent complètes, ex. "rooms.action.configure"). Le loader les
// fusionne en un seul objet de traduction global — comportement identique à
// l'ancien fichier plat unique. Pour ajouter un domaine : créer le fichier dans
// en/ ET fr/, puis ajouter son nom ci-dessous.
//
// Ce découpage ne vaut QU'EN DEV : le build fusionne les domaines en un seul
// /assets/i18n/<lang>.json (scripts/i18n-bundle.js) pour ne faire qu'une requête
// au démarrage au lieu de 42, à poids identique. Staging et production sont
// branchés à l'identique (`i18nBundled: true` dans les deux environnements,
// même commande de build dans bitbucket-pipelines.yml) — seuls dev et local
// chargent encore les 42 fichiers.
const I18N_DOMAINS = [
  'about',
  'account',
  'auth',
  'become-host',
  'booking',
  'bookings',
  'breadcrumb',
  'cancel-conditions',
  'changelog',
  'common',
  'contact',
  'coupon-redeem',
  'coupons',
  'customer-invoices',
  'customer-payments',
  'file-upload',
  'footer',
  'home',
  'invoicing',
  'locale',
  'my-booking-page',
  'nav',
  'page-title',
  'partners',
  'permissions',
  'pricing',
  'rates',
  'roles',
  'room-availabilities-modal',
  'room-details',
  'room-page',
  'room-view-amenities',
  'rooms',
  'settings',
  'users',
  'validation',
  'vendor-card',
  'vendor-page',
  'vendor-privacy',
  'vendor-selector',
  'vendors-list',
  'weekdays',
];

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  getTranslation(langPath: string): Observable<Translation> {
    return environment.i18nBundled
      ? this.loadBundle(langPath)
      : this.loadDomains(langPath);
  }

  /** Fichier unique produit par le build. */
  private loadBundle(langPath: string): Observable<Translation> {
    return this.http.get<Translation>(`/assets/i18n/${langPath}.json`).pipe(
      retry({ count: 2, delay: (_, attempt) => timer(attempt * 200) }),
      // Repli sur les fichiers par domaine si le build n'a pas fusionné.
      catchError(() => {
        console.error(
          `[i18n] Fichier fusionné « ${langPath}.json » introuvable — repli sur les domaines.`,
        );
        return this.loadDomains(langPath);
      }),
    );
  }

  /** Un fichier par domaine, recollés à plat (dev). */
  private loadDomains(langPath: string): Observable<Translation> {
    return forkJoin(
      I18N_DOMAINS.map((domain) =>
        this.http
          .get<Translation>(`/assets/i18n/${langPath}/${domain}.json`)
          .pipe(
            // Un aléa réseau sur un seul domaine ne doit pas faire échouer le
            // chargement complet : sans ça, Transloco lève TranslationLoadError,
            // l'APP_INITIALIZER est rejeté et l'application ne démarre pas
            // (page blanche). On réessaie, puis on se rabat sur un objet vide :
            // au pire quelques clés s'affichent brutes, l'app reste utilisable.
            retry({ count: 2, delay: (_, attempt) => timer(attempt * 200) }),
            catchError(() => {
              console.error(
                `[i18n] Domaine « ${domain} » non chargé pour « ${langPath} » — clés manquantes.`,
              );
              return of({} as Translation);
            }),
          ),
      ),
    ).pipe(map((parts) => Object.assign({}, ...parts) as Translation));
  }
}
